import { AbstractControl, ValidatorFn } from "@angular/forms";
import { Provider } from "@angular/core";
import {
  LuxonDateAdapter,
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  MatLuxonDateAdapterOptions,
} from "@angular/material-luxon-adapter";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
} from "@angular/material/core";
import { DateTime } from "luxon";
import { isFutureDobDate, toDobIsoDate } from "./date-utils";

export const DOB_FALLBACK_LOCALE = "en-GB";

export const futureDobValidator: ValidatorFn = (control: AbstractControl) => {
  if (!control.value) return null;
  try {
    return isFutureDobDate(control.value) ? { futureDate: true } : null;
  } catch {
    return { invalidDate: true };
  }
};

const normalizeLocale = (locale: string): string =>
  locale.replace(/_/g, "-").toLowerCase();

const getLocaleCandidates = (): string[] => {
  if (typeof navigator === "undefined") return [];
  return [...(navigator.languages ?? []), navigator.language].filter(Boolean);
};

const isGregorianLocale = (locale: string): boolean => {
  try {
    return (
      new Intl.DateTimeFormat(locale).resolvedOptions().calendar === "gregory"
    );
  } catch {
    return false;
  }
};

const isSupportedDobLocale = (locale: string): boolean => {
  try {
    const localeInfo = new Intl.Locale(locale);
    return (
      localeInfo.language === "en" &&
      !!localeInfo.region &&
      isGregorianLocale(locale)
    );
  } catch {
    return false;
  }
};

type DobDatePart = "day" | "month" | "year";

const isDobDatePart = (type: string): type is DobDatePart =>
  type === "day" || type === "month" || type === "year";

export type DobDatePartsProvider = (
  locale: string
) => Intl.DateTimeFormatPart[];

const getIntlDateParts: DobDatePartsProvider = (locale) =>
  new Intl.DateTimeFormat(locale, {
    calendar: "gregory",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).formatToParts(new Date(Date.UTC(2025, 0, 19)));

const getFormatParts = (
  locale: string,
  parts: Intl.DateTimeFormatPart[]
): string[] => {
  const order = parts.map(({ type }) => type).filter(isDobDatePart);
  const tokens: Record<DobDatePart, string> = {
    day: "d",
    month: "M",
    year: "yyyy",
  };

  if (order.length !== 3 || new Set(order).size !== 3) {
    throw new Error(`Unsupported DOB date order for locale ${locale}`);
  }

  return order.map((part) => tokens[part]);
};

const getDateSeparator = (parts: Intl.DateTimeFormatPart[]): string =>
  parts.find(({ type }) => type === "literal")?.value ?? "/";

/** The date parts provider is injectable so tests can supply locale shapes. */
export const getNumericFormats = (
  locale: string,
  getParts: DobDatePartsProvider = getIntlDateParts
): string[] => {
  const parts = getParts(locale);
  const [first, second, third] = getFormatParts(locale, parts);
  const separator = getDateSeparator(parts);
  if (separator !== "/" && separator !== "-" && separator !== ".") {
    throw new Error(`Unsupported DOB separator for locale ${locale}`);
  }
  const separators = [separator, "/", "-", "."].filter(
    (value, index, values) => values.indexOf(value) === index
  );

  return separators.flatMap((dateSeparator) => [
    `${first}${dateSeparator}${second}${dateSeparator}${third}`,
    [first, second, third]
      .map((part) => (part === "d" || part === "M" ? `${part}${part}` : part))
      .join(dateSeparator),
  ]);
};

const FALLBACK_NUMERIC_FORMATS = getNumericFormats(DOB_FALLBACK_LOCALE, () => [
  { type: "day", value: "19" },
  { type: "literal", value: "/" },
  { type: "month", value: "1" },
  { type: "literal", value: "/" },
  { type: "year", value: "2025" },
]);

export interface DobLocaleResolution {
  locale: string;
  isSupported: boolean;
}

/**
 * The overrides exist so that locale resolution can be exercised without
 * patching the global `Intl` or `navigator` objects. The Angular vitest runner
 * shares one worker across spec files, so global spies are not reliably
 * applied or restored.
 */
export interface DobLocaleOverrides {
  getParts?: DobDatePartsProvider;
  getCandidates?: () => string[];
}

export const resolveDobLocale = (
  locale?: string,
  {
    getParts = getIntlDateParts,
    getCandidates = getLocaleCandidates,
  }: DobLocaleOverrides = {}
): DobLocaleResolution => {
  const candidates = locale ? [locale] : getCandidates();

  for (const candidate of candidates) {
    const normalizedLocale = normalizeLocale(candidate);
    if (isSupportedDobLocale(normalizedLocale)) {
      try {
        getNumericFormats(normalizedLocale, getParts);
        return {
          locale: new Intl.Locale(normalizedLocale).toString(),
          isSupported: true,
        };
      } catch {
        continue;
      }
    }
  }

  return { locale: DOB_FALLBACK_LOCALE, isSupported: false };
};

export const getDobLocale = (locale?: string): string =>
  resolveDobLocale(locale).locale;

interface LocaleWeekInfo {
  firstDay: number;
}

type LocaleWithWeekInfo = Intl.Locale & {
  getWeekInfo?: () => LocaleWeekInfo;
  weekInfo?: LocaleWeekInfo;
};

const getFirstDayOfWeek = (locale: string): number => {
  try {
    const localeInfo = new Intl.Locale(locale) as LocaleWithWeekInfo;
    const weekInfo = localeInfo.getWeekInfo?.() ?? localeInfo.weekInfo;
    if (weekInfo && Number.isInteger(weekInfo.firstDay)) {
      return weekInfo.firstDay === 7 ? 0 : weekInfo.firstDay;
    }
  } catch {}

  return 0;
};

export const getDobDateAdapterOptions = (
  locale: string = DOB_FALLBACK_LOCALE
): MatLuxonDateAdapterOptions => ({
  useUtc: true,
  firstDayOfWeek: getFirstDayOfWeek(locale),
  defaultOutputCalendar: "gregory",
});

export class DobDateAdapter extends LuxonDateAdapter {
  override parse(
    value: unknown,
    parseFormat: string | string[]
  ): DateTime | null {
    if (typeof value !== "string" || value.length === 0) {
      return super.parse(value, parseFormat);
    }

    const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
    for (const format of formats) {
      const parsedDate = DateTime.fromFormat(value, format, {
        locale: this.locale,
        outputCalendar: "gregory",
        zone: "utc",
      });
      if (parsedDate.isValid) return parsedDate;
    }

    return this.invalid();
  }
}

export const formatDobForDisplay = (
  value: Date | string | number,
  locale?: string
): string => {
  if (value === "") return "";

  let date: DateTime;
  try {
    date =
      typeof value === "string"
        ? DateTime.fromISO(toDobIsoDate(value), { zone: "utc" })
        : DateTime.fromJSDate(new Date(value), { zone: "utc" });
  } catch {
    return "";
  }

  if (!date.isValid) return "";

  const localeResolution = resolveDobLocale(locale);
  const localizedDate = date.setLocale(localeResolution.locale);

  return localeResolution.isSupported
    ? localizedDate.toLocaleString(DateTime.DATE_MED)
    : localizedDate.toFormat("d MMMM yyyy");
};

export const getDobDateFormats = (locale?: string): MatDateFormats => {
  const effectiveLocale = getDobLocale(locale);
  // The resolved locale can still be unusable (for example when the fallback
  // locale itself formats with an unsupported separator), so degrade to the
  // day-first defaults instead of throwing.
  let dateInput: string[];
  try {
    dateInput = getNumericFormats(effectiveLocale);
  } catch {
    dateInput = FALLBACK_NUMERIC_FORMATS;
  }
  const paddedDateInput = dateInput[1];

  if (!paddedDateInput) {
    throw new Error(`Missing padded DOB format for locale ${effectiveLocale}`);
  }

  return {
    parse: { dateInput },
    display: {
      dateInput: paddedDateInput,
      monthYearLabel: "MMM yyyy",
      dateA11yLabel: paddedDateInput,
      monthYearA11yLabel: "MMM yyyy",
    },
  };
};

const DOB_PLACEHOLDER_TOKENS: Record<string, string> = {
  yyyy: "YYYY",
  dd: "DD",
  MM: "MM",
  d: "D",
  M: "M",
};

export const getDobPlaceholder = (locale?: string): string =>
  getDobDateFormats(locale).display.dateInput.replace(
    /yyyy|dd|MM|d|M/g,
    (token) => DOB_PLACEHOLDER_TOKENS[token] ?? token
  );

export const DOB_DATE_PROVIDERS: Provider[] = [
  {
    provide: DateAdapter<DateTime>,
    useClass: DobDateAdapter,
  },
  {
    provide: MAT_DATE_FORMATS,
    useFactory: getDobDateFormats,
    deps: [MAT_DATE_LOCALE],
  },
  {
    provide: MAT_DATE_LOCALE,
    useFactory: getDobLocale,
  },
  {
    provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
    useFactory: getDobDateAdapterOptions,
    deps: [MAT_DATE_LOCALE],
  },
];
